import { useMutation, useQueryClient } from '@tanstack/react-query'
import { ItemssharepointApi } from '../../../generated-types/apis/ItemssharepointApi'
import {
  type APISharepointCreationRequest,
  type APISharepointUpdateRequest,
} from '../../../generated-types/models'
import {
  Configuration,
  type InitOverrideFunction,
  type RequestOpts,
  ResponseError,
} from '../../../generated-types/runtime'
import { getAccessToken, refreshAccessToken, getApiBasePath } from '../../../api/authTokens'
import LogTool from '../../../logger/logTools'
import { createSharepoint } from '../../Item/utils/functions'
import { type Sharepoint } from '../../Item/utils/types'
import { sharepointKeys } from '../../Sharepoint/utils/queryKeys'
import { apiClient } from '../../../api'

const log = new LogTool({ context: 'useSharepointConfirmHandlers', enable: true, logLevel: 'warn' })

/**
 * API client that injects auth headers and retries once after refreshing JWTs.
 */
class AuthenticatedSharepointApi extends ItemssharepointApi {
  constructor() {
    super(
      new Configuration({
        basePath: getApiBasePath(),
      })
    )
  }

  private withAuthorization(context: RequestOpts, token?: string): RequestOpts {
    if (!token) {
      return context
    }

    return {
      ...context,
      headers: {
        ...context.headers,
        Authorization: `Bearer ${token}`,
      },
    }
  }

  protected override async request(
    context: RequestOpts,
    initOverrides?: RequestInit | InitOverrideFunction
  ): Promise<Response> {
    const token = await getAccessToken()
    const contextWithToken = this.withAuthorization(context, token)

    try {
      return await super.request(contextWithToken, initOverrides)
    } catch (error) {
      if (error instanceof ResponseError && error.response?.status === 401) {
        log.info('Received 401 from Sharepoint API, attempting token refresh')

        const refreshedToken = await refreshAccessToken()
        if (!refreshedToken) {
          log.warn('Token refresh failed for Sharepoint API request')
          throw error
        }

        const retriedContext = this.withAuthorization(context, refreshedToken)
        return super.request(retriedContext, initOverrides)
      }

      throw error
    }
  }
}

const sharepointsApi = new AuthenticatedSharepointApi()

type CreateVariables = APISharepointCreationRequest
type UpdateVariables = { sharepointId: number | string; payload: APISharepointUpdateRequest }

const isCreatePayload = (value: any): value is CreateVariables =>
  Boolean(value && Array.isArray(value.articles) && typeof value.shareDuration !== 'undefined')

const isUpdateVariables = (value: any): value is UpdateVariables =>
  Boolean(
    value &&
    (typeof value.sharepointId === 'number' || typeof value.sharepointId === 'string') &&
    isCreatePayload(value.payload ?? {})
  )

const getSharepointId = (sharepointId: number | string): number => {
  if (typeof sharepointId === 'number') {
    return sharepointId
  }

  const numericId = Number(String(sharepointId).replace(/\/$/, '').split('/').filter(Boolean).pop())

  if (Number.isNaN(numericId)) {
    throw new Error(`Invalid sharepoint identifier provided: ${sharepointId}`)
  }

  return numericId
}

/**
 * Shared helper hook for creating sharepoints via react-query + generated API client.
 */
export function useSharepointCreateHandler(
  setWaiting: React.Dispatch<React.SetStateAction<boolean>>
): {
  runCreateFlow: (payload: CreateVariables | (() => Promise<any>)) => Promise<Sharepoint>
} {
  const queryClient = useQueryClient()

  const createMutation = useMutation<Sharepoint, Error, CreateVariables>({
    mutationFn: async payload => {
      // TODO: fix this BS
      // const response = await apiClient.post('v1/items/sharepoints/', payload)
      const response = await sharepointsApi.v1ItemsSharepointsCreate({
        aPISharepointCreationRequest: payload,
      })
      return createSharepoint(response)
    },
    onMutate: () => setWaiting(true),
    onSettled: () => setWaiting(false),
    onSuccess: async sharepoint => {
      await queryClient.invalidateQueries({ queryKey: sharepointKeys.lists() })
      if (sharepoint?.key) {
        await queryClient.invalidateQueries({
          queryKey: sharepointKeys.detail(sharepoint.key),
        })
      }
      log.info('Sharepoint created successfully')
    },
    onError: error => {
      log.error('Error creating sharepoint:', error)
    },
  })

  const runCreateFlow = async (
    payloadOrExecutor: CreateVariables | (() => Promise<any>)
  ): Promise<Sharepoint> => {
    if (typeof payloadOrExecutor === 'function') {
      setWaiting(true)
      try {
        const result = await payloadOrExecutor()
        if (isCreatePayload(result)) {
          return await createMutation.mutateAsync(result)
        }
        // legacy flow already executed request
        return result as Sharepoint
      } finally {
        setWaiting(false)
      }
    }

    return await createMutation.mutateAsync(payloadOrExecutor)
  }

  return { runCreateFlow }
}

/**
 * Shared helper hook for updating sharepoints via react-query + generated API client.
 */
export function useSharepointUpdateHandler(
  setWaiting: React.Dispatch<React.SetStateAction<boolean>>
): {
  runUpdateFlow: (variables: UpdateVariables | (() => Promise<any>)) => Promise<Sharepoint>
} {
  const queryClient = useQueryClient()

  const updateMutation = useMutation<Sharepoint, Error, UpdateVariables>({
    mutationFn: async ({ sharepointId, payload }) => {
      const id = getSharepointId(sharepointId)

      await sharepointsApi.v1ItemsSharepointsUpdate({
        id,
        aPISharepointUpdateRequest: payload,
      })

      const refreshed = await sharepointsApi.v1ItemsSharepointsRetrieve({ id })
      return createSharepoint(refreshed as any)
    },
    onMutate: () => setWaiting(true),
    onSettled: () => setWaiting(false),
    onSuccess: async sharepoint => {
      await queryClient.invalidateQueries({ queryKey: sharepointKeys.lists() })
      if (sharepoint?.key) {
        await queryClient.invalidateQueries({
          queryKey: sharepointKeys.detail(sharepoint.key),
        })
      }
      log.info('Sharepoint updated successfully')
    },
    onError: error => {
      log.error('Error updating sharepoint:', error)
    },
  })

  const runUpdateFlow = async (
    variablesOrExecutor: UpdateVariables | (() => Promise<any>)
  ): Promise<Sharepoint> => {
    if (typeof variablesOrExecutor === 'function') {
      setWaiting(true)
      try {
        const result = await variablesOrExecutor()
        if (isUpdateVariables(result)) {
          return await updateMutation.mutateAsync(result)
        }
        return result as Sharepoint
      } finally {
        setWaiting(false)
      }
    }

    return await updateMutation.mutateAsync(variablesOrExecutor)
  }

  return { runUpdateFlow }
}
